import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import api from "../lib/api";

export const EnquiryForm = ({ defaultInterest = "", source = "website", dark = false, onDone }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    interest: defaultInterest,
    preferred_contact: "Call",
    message: "",
  });

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source });
      toast.success("Thank you! Our counsellor will reach out shortly.");
      setForm({ name: "", mobile: "", email: "", interest: defaultInterest, preferred_contact: "Call", message: "" });
      onDone && onDone();
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm text-[#0A192F] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F5132]/40 focus:border-[#0F5132] transition";

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="enquiry-form">
      <div>
        <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Full Name *</label>
        <input data-testid="enquiry-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <div>
          <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Mobile Number *</label>
          <input data-testid="enquiry-mobile-input" className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="+91" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Email</label>
          <input data-testid="enquiry-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Course Interested In</label>
        <select data-testid="enquiry-course-select" className={inputCls} value={form.interest} onChange={set("interest")}>
          <option value="">Select a course</option>
          {courses.map((c) => (
            <option key={c.id} value={c.title}>
              {c.title}
            </option>
          ))}
          <option value="Professional Services">Professional Services</option>
          <option value="Not sure yet">Not sure yet — need guidance</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#334E68] mb-1.5">Preferred Contact Method</label>
        <div className="flex gap-2" data-testid="enquiry-contact-method">
          {["Call", "WhatsApp", "Email"].map((m) => (
            <button
              type="button"
              key={m}
              data-testid={`enquiry-contact-${m.toLowerCase()}`}
              onClick={() => setForm({ ...form, preferred_contact: m })}
              className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
                form.preferred_contact === m
                  ? "bg-[#0F5132] text-white border-[#0F5132]"
                  : "bg-white text-[#334E68] border-slate-300 hover:border-[#0F5132]"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="enquiry-submit-btn"
        className="w-full py-3 rounded-lg bg-[#0A192F] text-white font-display font-bold text-sm tracking-wide hover:bg-[#0F5132] transition-colors disabled:opacity-60"
      >
        {loading ? "Submitting..." : "Get Course Details"}
      </button>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        By submitting, you consent to SterlingNest contacting you about courses and services.
      </p>
    </form>
  );
};

export const EnquiryModal = ({ trigger, defaultInterest = "", source = "enquiry-modal" }) => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md bg-white" data-testid="enquiry-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold text-[#0A192F]">Get Course Details</DialogTitle>
          <p className="text-sm text-[#627D98]">Practical Learning • Hands-on Assignments • Career-focused Training</p>
        </DialogHeader>
        <EnquiryForm defaultInterest={defaultInterest} source={source} onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};
